import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import {
    eventCardLayoutAtom,
    operatedLikesListAtom,
} from "src/features/Event/event.atom";
import type { Bookmark, Like } from "src/features/Event/event.type";
import getDateTimeForDisplayOnEventCard from "src/utils/getDateTimeForDisplayOnEventCard";
import getCurrentUserId from "src/utils/currentUserId";
import EventCardSmall from "../EventCardSmall/EventCardSmall";
import EventCardLarge from "../EventCardLarge/EventCardLarge";

type EventCardV2Props = {
    event: {
        event_id: number;
        title: string;
        place: string;
        start_time: string;
        finish_time: string;
        thumbnail: string;
        eventType: string;
    };
    likes: Like[];
    comments_count?: number;
    bookmarks?: Bookmark[];
    user: {
        userId: number;
        username: string;
        userIconPath: string;
        certification: string;
    };
    onlySmall?: boolean;
    noLikes?: boolean;
    noShadow?: boolean;
};

const EventCardV2: React.FC<EventCardV2Props> = (props) => {
    const navigate = useNavigate();
    const currentUserId = getCurrentUserId();

    const eventCardLayout = useAtomValue(eventCardLayoutAtom);

    const [operatedLikesList, setOperatedLikesList] = useAtom(
        operatedLikesListAtom
    );

    // operatedLikesListをチェックして、存在しない場合はprops.likesをチェックする
    const [isLiked, setIsLiked] = useState<boolean>(
        operatedLikesList[props.event.event_id]?.isLiked ??
            props.likes.some((like: { user_id: number }) => {
                return like.user_id === currentUserId;
            })
    );

    const [isLikesCount, setLikesCount] = useState<number>(
        operatedLikesList[props.event.event_id]?.likesCount ??
            props.likes.length
    );

    useEffect(() => {
        if (operatedLikesList[props.event.event_id]) {
            setIsLiked(operatedLikesList[props.event.event_id].isLiked);
            setLikesCount(operatedLikesList[props.event.event_id].likesCount);
        }
    }, [operatedLikesList, props.event.event_id]);

    const handleCardClicked = () => {
        navigate(`/event/${props.event.event_id}`);
    };

    const [isModalOpen, setModalOpen] = useState(false);

    const handleMenuButton = () => {
        setModalOpen(true);
    };

    if (eventCardLayout === "small" || props.onlySmall) {
        return (
            <EventCardSmall
                handleCardClicked={handleCardClicked}
                setOperatedLikesList={setOperatedLikesList}
                handleMenuButton={handleMenuButton}
                event={props.event}
                eventDayTime={getDateTimeForDisplayOnEventCard(
                    props.event.start_time,
                    props.event.finish_time
                )}
                user={props.user}
                isLiked={isLiked}
                isLikesCount={isLikesCount}
                isModalOpen={isModalOpen}
                setModalOpen={setModalOpen}
                noShadow={props.noShadow}
                noLikes={props.noLikes}
            />
        );
    }

    return (
        <EventCardLarge
            handleCardClicked={handleCardClicked}
            handleMenuButton={handleMenuButton}
            event={props.event}
            eventDayTime={getDateTimeForDisplayOnEventCard(
                props.event.start_time,
                props.event.finish_time
            )}
            user={props.user}
            isLiked={isLiked}
            isLikesCount={isLikesCount}
            bookmarks={props.bookmarks ?? []}
            commentsCount={props.comments_count ?? 0}
            isModalOpen={isModalOpen}
            setModalOpen={setModalOpen}
        />
    );
};

export default EventCardV2;
